/** @typedef {{ valueColors: Object<string, string> }} ColumnColorRule */
/** @typedef {Object<string, ColumnColorRule>} ColumnColoring Physical columns, exact displayed values. */

const SAFE_HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const isRecord = candidate => candidate !== null && typeof candidate === 'object' && !Array.isArray(candidate);
const owns = (record, key) => Object.prototype.hasOwnProperty.call(record, key);

/** Sanitize optional persisted config; an empty explicit rule still disables heuristic coloring. */
export function normalizeColumnColoring(columnColoring) {
  if (!isRecord(columnColoring)) return undefined;
  return Object.fromEntries(Object.entries(columnColoring)
    .filter(([, rule]) => isRecord(rule))
    .map(([column, rule]) => [column, {
      valueColors: Object.fromEntries(Object.entries(isRecord(rule.valueColors) ? rule.valueColors : {})
        .filter(([, hex]) => typeof hex === 'string' && SAFE_HEX.test(hex))),
    }]));
}

/** Null means this column keeps its legacy presentation; an empty rule opts into neutral pills. */
export function columnColorRule(columnColoring, column) {
  return isRecord(columnColoring) && owns(columnColoring, column) && isRecord(columnColoring[column])
    ? columnColoring[column] : null;
}

/** Exact matches only. Unknown/null values never borrow another status's color. */
export function columnStatusStyle(rule, displayedValue) {
  if (displayedValue == null || !isRecord(rule?.valueColors)) return {};
  const label = String(displayedValue);
  const hex = owns(rule.valueColors, label) ? rule.valueColors[label] : null;
  if (typeof hex !== 'string' || !SAFE_HEX.test(hex)) return {};
  const expanded = hex.length === 4 ? hex.slice(1).split('').map(digit => digit + digit).join('') : hex.slice(1);
  const channels = expanded.match(/../g).map(pair => {
    const channel = parseInt(pair, 16) / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  const luminance = channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  // Select the higher-contrast foreground for the authored background in either theme.
  return { backgroundColor: hex, color: luminance > 0.179 ? '#000000' : '#ffffff' };
}
